/**
 * `NewCaseDialog` — the "New Case" dialog. Creates a case by RESOLVING-OR-CREATING
 * the whole client → matter chain (operator 2026-07-06): the user specifies BOTH
 * a client and a matter upfront, each as pick-an-EXISTING (by id) OR type-a-NEW
 * name (auto-created + deduped server-side). Smart-defaulted from the active
 * scope (matter → both prefilled; client → client prefilled; global → free) but
 * editable.
 *
 * ⚠ Both are OPTIONAL, and that is deliberate — this line used to read "Both
 * required, never blank", which was the 2026-07-06 shape and has been FALSE
 * since D-8 (assignment-pending) shipped with Flat Case Storage on 2026-07-08.
 * `buildCaseCreateParams` returns a valid PARENTLESS payload when no client is
 * chosen, and the case is born with `clientId: null` — a first-class state the
 * registry (`clientId?: string | null`), `GET /cases?unassigned=true`,
 * `PATCH /cases/:caseId/parentage` and harn-service's relaxed `validateTopology`
 * all exist to serve. See the shape note on `buildCaseCreateParams` below and
 * `ai-docs/flat-case-storage-lexserver-plan.md`.
 *
 * Backend contract: `case.create` accepts clientId|clientName (+ isPerson for a
 * new client) and matterId|matterName (+ matterType for a new matter), and
 * returns client/matter {id,name,created}. Handoff:
 * ai-docs/handoffs/case-create-resolve-or-create-chain-handoff-2026-07-06.md
 * (the new-name path needs that runtime work; the existing-id path works today).
 *
 * Modal by design (operator 2026-07-03): a click outside does NOT close it —
 * only Cancel / X / Esc. The op-verb button + spinner is the in-flight cue.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Loader2, Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  dialogOverlayCls,
  dialogPanelCls,
  dialogHeaderCls,
  dialogTitleCls,
  dialogCloseBtnCls,
  dialogLabelCls,
  dialogInputCls,
  dialogFooterCls,
  dialogCancelBtnCls,
  dialogPrimaryBtnCls,
} from "@/components/context-pane/dialog-styles";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { useActiveContextStore } from "@/stores/useActiveContextStore";
import { useAuthIdentity } from "@/hooks/useAuthIdentity";
import { useToastStore } from "@/stores/useToastStore";
import { useClients } from "@/hooks/useClients";
import { useMatters } from "@/hooks/useMatters";
import { useMatterVocabulary } from "@/hooks/useMatterVocabulary";
import { normalizeMatterType } from "@/lib/case-labels";
import { normalizeDisplayName } from "@/lib/workspace/display-name";
import { resolveDisplayName } from "@/lib/entity-display";
import { useLanguageStore } from "@/stores/useLanguageStore";
import {
  NameOrPickField,
  hasNewName,
  isExplicitCreate,
  type NameOrPickValue,
  type SuggestionRow,
} from "@/components/ui/NameOrPickField";
import type { MatterVocabularyRow } from "@/lib/lexserver-client";
import type { CaseConflictHit, CaseCreateParams, CaseCreateResult } from "@/types/gateway";

/** The runtime-valid matter types (runtime `VALID_MATTER_TYPES`) — the full
 *  9-value kernel `MatterTypeSchema` since the 6→9 widen (`8a41b393`,
 *  2026-07-08); order mirrors the kernel enum + org-admin `MATTER_TYPES`. A new
 *  matter with any other type 400s at the runtime. */
export const RUNTIME_MATTER_TYPES = [
  "civil",
  "labor",
  "tax",
  "contract",
  "advisory",
  "corporate",
  "realestate",
  "compliance",
  "other",
] as const;

interface PartyRow {
  name: string;
  role: string;
}

interface NewCaseDialogProps {
  /** Smart-default seeds from the active scope (all optional → global). */
  initialClientId?: string;
  initialMatterId?: string;
  initialMatterType?: string;
  onClose: () => void;
  /** Fired on a successful create so the parent can refresh its case list. */
  onCreated: (result: CaseCreateResult) => void;
}

/**
 * Pure param-builder — maps the dialog's client/matter selections to the
 * `case.create` params. Exported for unit testing.
 *
 * Two shapes (runtime handback §3):
 *   - "create now, assign later" — when NO client is chosen, returns
 *     `{ title, createdBy, matterType }` (+ parties). The case is born
 *     unassigned (clientId/matterId null) and lands in the Unassigned bucket.
 *     ⚠ `matterType` is REQUIRED here and was missing until 2026-08-10, which
 *     is why a fresh org could not create its first case: with no matter to
 *     inherit a type from, the gateway refuses (`case.ts:610-622`). Sending it
 *     does NOT create a parent — `hasParentInput` stays false.
 *   - resolve-or-create chain — when a client IS chosen, emits clientId|clientName
 *     (+ isPerson for a new client) and, when a matter is ALSO chosen,
 *     matterId|matterName (+ matterType for a new matter). A matter is picked
 *     UNDER a client, so a matter without a client is never emitted (the UI can't
 *     reach that state; if constructed anyway, the matter is ignored → title-only).
 *
 * Returns null ONLY when the title is empty.
 */
export function buildCaseCreateParams(input: {
  clientValue: NameOrPickValue;
  isPerson: boolean;
  matterValue: NameOrPickValue;
  matterType: string;
  title: string;
  createdBy: string;
  parties: { name: string; role?: string }[];
}): CaseCreateParams | null {
  const title = input.title.trim();
  if (!title) return null;
  const params: CaseCreateParams = { title, createdBy: input.createdBy };

  if (input.clientValue) {
    if (input.clientValue.mode === "existing") {
      params.clientId = input.clientValue.id;
    } else {
      params.clientName = input.clientValue.name;
      params.isPerson = input.isPerson;
    }

    // A matter is OPTIONAL and only exists UNDER a client — never sent alone.
    if (input.matterValue) {
      if (input.matterValue.mode === "existing") {
        params.matterId = input.matterValue.id;
      } else {
        params.matterName = input.matterValue.name;
        params.matterType = input.matterType;
      }
    } else {
      // CLIENT BUT NO ESFERA — the sibling of the parentless hole, found live
      // 2026-08-10 while proving D-98. The real rule is not "no client ⇒ send a
      // type", it is **no MATTER ⇒ send a type**: the scaffold refuses with
      // "caseType is required for mode=create when the case has no parent
      // client+matter to resolve it from", and a client alone resolves nothing.
      //
      // `176cc58d1` fixed only the fully-parentless shape, so this one stayed
      // broken for EVERY org, new or old — measured on an EXISTING client too,
      // which is what proves it is not a D-98 regression. Its symptom is the
      // worst kind: with a committed ＋ Criar client the parent IS created and
      // then the case fails, so the lawyer retries into a growing pile of real
      // clients and a generic "não foi possível criar o caso".
      params.matterType = input.matterType;
    }
  } else {
    // PARENTLESS — and this branch is why a fresh org could not create its
    // first case (2026-08-10). It used to emit a title-only payload, which the
    // gateway rejects: `case.ts:610-622` refuses when there is no parent input
    // AND no `matterType`, because a case with no matter has nothing to
    // inherit its type FROM. The server supports parentless creation and was
    // naming the one input that makes it legal; the dialog simply never sent
    // it, so the request was malformed before it left the browser.
    //
    // ⚠ Not a widening of the contract: `hasParentInput` stays false, so the
    // gateway's `resolveParents` branch still does not run and the case is
    // still born unassigned. The only change is supplying the type the server
    // asked for.
    params.matterType = input.matterType;
  }

  // ⚖ D-98 — authorise parent creation ONLY on an explicit create act.
  //
  // `isExplicitCreate` is `mode:"new"`, which since D-98 is reachable only
  // through `commitNew` (the ＋ Criar row, a suggestion row, or Enter on a name
  // exactly matching a RENDERED suggestion). A `typed` value — free text still
  // in flight — deliberately does NOT set this: the request goes out without it,
  // the gateway refuses with its near-miss suggestion, and `humanizeCaseCreateError`
  // guides the lawyer to the ＋ Criar row.
  //
  // ⚠ The predicate is the whole protection. Widening it to "a name is present"
  // (`hasNewName`, or a truthiness test on `clientName`) mints a client from a
  // half-typed word, permanently and org-wide, while the dialog reports success.
  // ⚠ Scoped to what was ACTUALLY emitted: a matter is never sent without its
  // client (see the branch above), so an orphan `mode:"new"` matter must not
  // raise the flag for a request that carries no matter at all.
  if (input.clientValue && (isExplicitCreate(input.clientValue) || isExplicitCreate(input.matterValue))) {
    params.createParents = true;
  }

  if (input.parties.length > 0) params.parties = input.parties;
  return params;
}

/**
 * Turn a gateway refusal into something a lawyer can act on.
 *
 * ## Why this exists
 *
 * The dialog used to render `err.message` verbatim, which is how this reached a
 * real user's screen (2026-08-10):
 *
 *   > no client named "ZZ PROBE" exists in this org — did you mean "ZZ PROBE
 *   > Cliente (throwaway)"?. Creating one is a separate, explicit act: re-send
 *   > with createParents:true if you really mean to add a new client.
 *
 * `createParents:true` is an API parameter. "Re-send with" is not an action a
 * lawyer can take. That is an internal contract leaking through the UI.
 *
 * ## The two rules this follows
 *
 * 1. **KEEP THE DID-YOU-MEAN.** The server's suggestion is the genuinely useful
 *    half — it names the record the user probably meant. Translating the prose
 *    around it must never drop it.
 * 2. **AN UNRECOGNISED ERROR STILL SPEAKS.** Anything unmapped falls back to the
 *    generic message rather than being swallowed. Silence, or a dialog that
 *    closes as if it worked, would be worse than clumsy wording: the case does
 *    not exist and the user must know that.
 *
 * ⚠ Deliberately matches on the SERVER'S PROSE, which is brittle by nature. The
 * durable fix is a machine-readable `code` on the error envelope — raised with
 * `lexserver` as a separate thread, not blocked on here. Until then the fallback
 * in rule 2 is what keeps a wording change on their side from turning into a
 * blank dialog on ours.
 */
export function humanizeCaseCreateError(
  err: unknown,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const raw = err instanceof Error && err.message ? err.message : "";
  if (!raw) return t("newCase.errors.generic");

  // Preserve the server's did-you-mean when present.
  const suggestion = raw.match(/did you mean "([^"]+)"/i)?.[1];

  // A parentless case with no esfera. Should now be unreachable from the UI
  // (the select renders in that state), so treat it as a real failure worth
  // naming rather than a state to paper over.
  if (/matterType is required/i.test(raw)) {
    return t("newCase.errors.matterTypeRequired");
  }

  // ⚖ D-98 — THE MATTER ARM IS MATCHED FIRST, AND THE ORDER IS LOAD-BEARING.
  //
  // The server refuses an unresolved ESFERA with "no matter named …" and an
  // unresolved CLIENT with "no client named …" — but both refusals also carry
  // the shared `createParents:true` sentence. So the broader client patterns
  // below match a MATTER refusal too, and before this split the lawyer was told
  // "Esse cliente ainda não existe" while the server was refusing on the esfera
  // — a confident, wrong diagnosis on top of a dead end. `case-storage` hit this
  // personally: client picked from the live list, still told it did not exist.
  if (/no matter named/i.test(raw)) {
    return suggestion
      ? t("newCase.errors.matterUnknownWithSuggestion", { suggestion })
      : t("newCase.errors.matterUnknown");
  }

  // The client the user named does not exist, and creating one is an explicit
  // act the server will not infer. Say that in the user's terms.
  if (/createParents/i.test(raw) || /does not exist|no client named/i.test(raw)) {
    return suggestion
      ? t("newCase.errors.clientUnknownWithSuggestion", { suggestion })
      : t("newCase.errors.clientUnknown");
  }

  return t("newCase.errors.generic");
}

/**
 * Compose the success-toast text — surfaces an auto-created client/matter when
 * the runtime reports one (`created:true`), else the plain "case created" line.
 * FAILS SOFT: when `result.client`/`result.matter` are absent (id-only path or
 * an older runtime), it degrades to the plain toast, never throws. Pure (`t`
 * injected) so it is unit-testable — mirrors the `buildCaseCreateParams` seam.
 */
export function selectSuccessToast(
  result: Pick<CaseCreateResult, "title" | "client" | "matter">,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const created: string[] = [];
  if (result.client?.created) created.push(t("newCase.createdClient", { name: result.client.name }));
  if (result.matter?.created) created.push(t("newCase.createdMatter", { name: result.matter.name }));
  return created.length > 0
    ? t("newCase.toastCreatedChain", { title: result.title, chain: created.join(" · ") })
    : t("newCase.toastCreated", { title: result.title });
}

/** Portfolio-name → matterType aliases (normalized via `normalizeDisplayName`)
 *  beyond the locale label and the enum id themselves. Entries for types not
 *  currently offered (labor/tax) are inert until the runtime widens the enum —
 *  they light up for free when the 9-type flip lands. */
const MATTER_TYPE_NAME_ALIASES: Record<string, string[]> = {
  civil: ["contencioso civel"],
  labor: ["contencioso trabalhista"],
  tax: ["contencioso tributario"],
  contract: ["contratos", "contrato"],
  advisory: ["consultoria", "consultas", "pareceres"],
};

/**
 * Category inference (lexiun-solution.md §3.1, operator 2026-07-07): a matter
 * is a practice-area PORTFOLIO, so users naturally type category-style names
 * ("Contratos", "Contencioso Cível", "Cível"). When the typed NEW-matter name
 * matches an offered type's label, enum id, or alias (accent/case-insensitive),
 * return that type so the dialog can auto-select it. Pure — exported for tests.
 */
export function inferMatterTypeFromName(
  name: string,
  labels: Record<string, string>,
): string | undefined {
  const n = normalizeDisplayName(name);
  if (!n) return undefined;
  for (const [mt, label] of Object.entries(labels)) {
    const candidates = [mt, label, ...(MATTER_TYPE_NAME_ALIASES[mt] ?? [])];
    if (candidates.some((c) => normalizeDisplayName(c) === n)) return mt;
  }
  return undefined;
}

/**
 * Rank vocabulary rows for the suggestion list (matter-vocabulary handback §3):
 * same-user > same-org > platform. The server already sorts `usageCount desc,
 * name asc`; JS sort is stable, so ranking by tier alone preserves that
 * secondary order within a tier. Pure — exported for tests.
 */
export function rankMatterVocabulary(
  rows: MatterVocabularyRow[],
  ctx: { userId?: string; orgId?: string },
): MatterVocabularyRow[] {
  const tier = (r: MatterVocabularyRow): number => {
    if (ctx.userId && r.createdByUserId === ctx.userId) return 0;
    if (ctx.orgId && r.createdByOrgId === ctx.orgId) return 1;
    return 2; // platform (null creator) or any other-org row (not returned by the API)
  };
  return [...rows].sort((a, b) => tier(a) - tier(b));
}

/**
 * Build the ranked, filtered matter-name suggestions for the combobox:
 * ranked vocabulary, minus rows whose type isn't offered (off-enum/unknown) and
 * minus names the chosen client already has as a matter (those show in the
 * existing-matters section). Each suggestion is shown in the active locale via
 * `displayNames` (entity-identity Slice 1), falling back to the canonical `name`
 * — so the field matches + displays the same localized text the user picks.
 * Pure — exported for tests.
 */
export function buildMatterSuggestions(input: {
  vocabulary: MatterVocabularyRow[];
  existingMatterNames: string[];
  userId?: string;
  orgId?: string;
  offeredTypes: readonly string[];
  locale: string;
  labelFor: (matterType: string) => string;
}): SuggestionRow[] {
  const existingFolds = new Set(input.existingMatterNames.map(normalizeDisplayName));
  return rankMatterVocabulary(input.vocabulary, { userId: input.userId, orgId: input.orgId })
    .filter((r) => input.offeredTypes.includes(r.matterType))
    .map((r) => ({ name: resolveDisplayName(r, input.locale), matterType: r.matterType }))
    .filter((s) => !existingFolds.has(normalizeDisplayName(s.name)))
    .map((s) => ({ name: s.name, sublabel: input.labelFor(s.matterType) }));
}

/**
 * Resolve the matterType for a typed/picked NEW-matter name: an exact vocabulary
 * fold-match wins (authoritative — the canonical row knows its type), else fall
 * back to name inference. The vocab match folds against the canonical `name` AND
 * every locale's `displayName` — so a suggestion picked/typed in ANY locale
 * ("Contracts"/"Contratos"/"Contractual") still auto-types. Only ever returns an
 * OFFERED type. Pure — for tests.
 */
export function resolveMatterTypeForName(
  name: string,
  vocabulary: MatterVocabularyRow[],
  labels: Record<string, string>,
  offeredTypes: readonly string[],
): string | undefined {
  const n = normalizeDisplayName(name);
  if (!n) return undefined;
  // Only OFFERED rows are eligible, so an off-enum/unknown row can never shadow
  // an offered match with the same fold (and the returned type is always offered).
  const hit = vocabulary.find((r) => {
    if (!offeredTypes.includes(r.matterType)) return false;
    if (normalizeDisplayName(r.name) === n) return true;
    const dn = r.displayNames;
    return !!dn && [dn.pt, dn.en, dn.es].some((v) => v && normalizeDisplayName(v) === n);
  });
  if (hit) return hit.matterType;
  // Clamp the inference fallback to offered types too, so the contract ("only
  // ever returns an OFFERED type") holds regardless of the labels map passed.
  const inferred = inferMatterTypeFromName(name, labels);
  return inferred && offeredTypes.includes(inferred) ? inferred : undefined;
}

export function NewCaseDialog({
  initialClientId,
  initialMatterId,
  initialMatterType,
  onClose,
  onCreated,
}: NewCaseDialogProps) {
  const { t } = useTranslation();
  const rpc = useGatewayStore((s) => s.rpc);
  const navigate = useNavigate();
  const selectCase = useActiveContextStore((s) => s.selectCase);
  const { identity } = useAuthIdentity();
  const addToast = useToastStore((s) => s.addToast);
  const queryClient = useQueryClient();
  const lang = useLanguageStore((s) => s.lang);

  const { data: clients } = useClients();

  const [clientValue, setClientValue] = useState<NameOrPickValue>(null);
  const [isPerson, setIsPerson] = useState(false);
  const [matterValue, setMatterValue] = useState<NameOrPickValue>(null);
  const [matterType, setMatterType] = useState<string>(
    initialMatterType && (RUNTIME_MATTER_TYPES as readonly string[]).includes(initialMatterType)
      ? initialMatterType
      : "civil",
  );
  const [title, setTitle] = useState("");
  const [parties, setParties] = useState<PartyRow[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [review, setReview] = useState<{ result: CaseCreateResult; conflicts: CaseConflictHit[] } | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  // Existing matters are only pickable under an EXISTING client (a brand-new
  // client has none yet); the query auto-disables when clientId is undefined.
  const selectedClientId = clientValue?.mode === "existing" ? clientValue.id : undefined;
  const { data: matters } = useMatters(selectedClientId);
  // Platform-wide matter-name vocabulary → the "Assunto" suggestion list (§7).
  const { data: vocabulary } = useMatterVocabulary();

  const clientOptions = useMemo(
    () =>
      (clients ?? []).map((c) => ({
        id: c.clientId,
        label: c.name,
        sublabel: c.alias ? `@${c.alias}` : undefined,
      })),
    [clients],
  );
  const matterOptions = useMemo(
    () =>
      (matters ?? []).map((m) => ({
        id: m.matterId,
        // Entity-identity Slice 1: show the i18n displayName, fail-soft → name.
        label: resolveDisplayName(m, lang),
        // An existing matter can carry any of the 9 canonical types (not just
        // the 6 runtime-creatable ones); defaultValue falls back to the slug so
        // a future type without a label never renders the raw i18n key path.
        sublabel: t(`newCase.matterTypes.${normalizeMatterType(m.matterType)}`, {
          defaultValue: normalizeMatterType(m.matterType),
        }),
      })),
    [matters, t, lang],
  );
  // Ranked "Assunto" suggestions — canonical vocabulary names the user can pick
  // instead of typing; hidden are the client's existing matters (shown above,
  // matched by their DISPLAY name) and any type not yet creatable at the runtime.
  const matterSuggestions = useMemo(
    () =>
      buildMatterSuggestions({
        vocabulary: vocabulary ?? [],
        existingMatterNames:
          clientValue?.mode === "existing" ? (matters ?? []).map((m) => resolveDisplayName(m, lang)) : [],
        userId: identity?.userId,
        orgId: identity?.orgId,
        offeredTypes: RUNTIME_MATTER_TYPES,
        locale: lang,
        labelFor: (mt) => t(`newCase.matterTypes.${mt}`),
      }),
    [vocabulary, matters, clientValue, identity, t, lang],
  );

  // Smart-default seeds from the active scope, once the data resolves. A ref
  // guard prevents clobbering the user's edits on later query refetches.
  const seededClient = useRef(false);
  useEffect(() => {
    if (seededClient.current) return;
    if (!initialClientId) {
      seededClient.current = true;
      return;
    }
    const c = (clients ?? []).find((x) => x.clientId === initialClientId);
    if (c) {
      setClientValue({ mode: "existing", id: c.clientId, label: c.name });
      seededClient.current = true;
    }
  }, [initialClientId, clients]);

  const seededMatter = useRef(false);
  useEffect(() => {
    if (seededMatter.current) return;
    if (!initialMatterId) {
      seededMatter.current = true;
      return;
    }
    const m = (matters ?? []).find((x) => x.matterId === initialMatterId);
    if (m) {
      setMatterValue({ mode: "existing", id: m.matterId, label: resolveDisplayName(m, lang) });
      seededMatter.current = true;
    }
  }, [initialMatterId, matters, lang]);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // ⚖ D-98 affordance, NOT the security gate: the esfera TYPE select must be
  // present while the lawyer is still TYPING a new esfera name, otherwise the
  // form re-lays-out at the instant they commit. `createParents` is gated
  // separately on `isExplicitCreate` in `buildCaseCreateParams`.
  const isNewMatter = hasNewName(matterValue);
  // The dialog title is ALWAYS the neutral "Novo caso" (operator 2026-07-07,
  // superseding the earlier resolved-noun variant): the matterType-aware case
  // noun (D8b) confused more than it informed as a TITLE — the case is the
  // GENERIC unit of work (lexiun-solution.md §3.1), and the matter's category
  // is already visible in the field itself (option sublabels + type select).
  const dialogTitle = t("newCase.titleNeutral");

  const cleanedParties = useMemo(
    () =>
      parties
        .map((p) => ({ name: p.name.trim(), role: p.role.trim() || undefined }))
        .filter((p) => p.name.length > 0),
    [parties],
  );

  const params = buildCaseCreateParams({
    clientValue,
    isPerson,
    matterValue,
    matterType,
    title,
    createdBy: identity?.userId ?? "",
    parties: cleanedParties,
  });
  const canCreate = !!params && !creating;

  function switchToCase(result: CaseCreateResult) {
    // N1: the SHORT canonical (/cases/:id) for every case — the long URL is no
    // longer generated anywhere. That also lets the title-only ("create now,
    // assign later") case OPEN on creation: the old code could only build the
    // 3-id long URL, so an unassigned case closed the dialog into nothing.
    // CaseRouteResolver handles null parents (D-8).
    selectCase(result.clientId ?? null, result.matterId ?? null, result.caseId);
    navigate(`/cases/${result.caseId}`);
    onClose();
  }

  const handleCreate = useCallback(async () => {
    if (!rpc || !params || creating) return;
    if (!identity?.userId) {
      setError(t("newCase.errors.noIdentity"));
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const result = await rpc.caseCreate(params);
      // A create RESOLVES-OR-CREATES the client→matter chain, so it can surface a
      // NEW (or previously-uncached) client/matter. The runtime broadcasts only
      // `case.updated` — NOT `client.updated` — on this path (handback 2026-07-07
      // §6), so refresh the cross-cutting caches here deterministically. Without
      // this, the navigator's client/matter group headers render the raw id/slug
      // until a manual reload. Covers EVERY caller (navigator + matter surfaces).
      void queryClient.invalidateQueries({ queryKey: ["overview-cases"] });
      // `["cases"]` backs the flat lists — CasesListLayout (/cases), the Command
      // Palette, the case switcher — which read `fetchCases()` under a SEPARATE key.
      // Invalidate it too, else a just-created (esp. title-only/unassigned) case is
      // absent from those surfaces + the "assign later" switcher for the 60s staleTime.
      void queryClient.invalidateQueries({ queryKey: ["cases"] });
      void queryClient.invalidateQueries({ queryKey: ["clients"] });
      void queryClient.invalidateQueries({ queryKey: ["matters", result.clientId] });
      // A genuinely-new matter name may have added a vocabulary row server-side.
      void queryClient.invalidateQueries({ queryKey: ["matter-vocabulary"] });
      onCreated(result);
      // Coerce hint (§7): lexserver canonicalizes the matter name ("Cível" →
      // "Contencioso Cível"). When we sent a NEW name and it came back folding
      // differently, tell the user their assunto was organized under the
      // canonical term. A case-only fold change (typo casing) is not announced.
      if (
        params.matterName &&
        result.matter?.name &&
        normalizeDisplayName(result.matter.name) !== normalizeDisplayName(params.matterName)
      ) {
        addToast({
          text: t("newCase.matterCoerced", { name: result.matter.name }),
          kind: "info",
          autoDismissMs: 6000,
        });
      }
      const conflicts = result.conflicts ?? [];
      if (conflicts.length > 0) {
        setReview({ result, conflicts });
        setCreating(false);
        return;
      }
      addToast({ text: selectSuccessToast(result, t), kind: "success", autoDismissMs: 4000 });
      switchToCase(result);
    } catch (err) {
      setError(humanizeCaseCreateError(err, t));
      setCreating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rpc, params, creating, identity, onCreated, addToast, queryClient, t]);

  function acknowledgeAndOpen() {
    if (!review) return;
    addToast({ text: selectSuccessToast(review.result, t), kind: "success", autoDismissMs: 4000 });
    switchToCase(review.result);
  }

  const inputCls = dialogInputCls;
  const labelCls = dialogLabelCls;

  return (
    <div data-testid="new-case-dialog" className={dialogOverlayCls}>
      <div role="dialog" aria-modal="true" aria-label={dialogTitle} className={dialogPanelCls}>
        <div className={dialogHeaderCls}>
          <h2 className={dialogTitleCls}>{dialogTitle}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("actions.close")}
            className={dialogCloseBtnCls}
          >
            <X size={16} />
          </button>
        </div>

        {review ? (
          <ConflictReview conflicts={review.conflicts} onOpen={acknowledgeAndOpen} onClose={onClose} />
        ) : (
          <>
            {/* CLIENT — pick existing or type new (OPTIONAL: skip to create now,
                assign later — the case lands in the Unassigned bucket) */}
            <label className={labelCls}>
              {t("newCase.clientLabel")}{" "}
              <span className="font-normal normal-case tracking-normal text-cream-400">
                {t("newCase.optionalTag")}
              </span>
            </label>
            <NameOrPickField
              data-testid="new-case-client"
              options={clientOptions}
              value={clientValue}
              onChange={(v) => {
                setClientValue(v);
                // A different/new client invalidates the matter selection.
                setMatterValue(null);
              }}
              placeholder={t("newCase.clientPlaceholder")}
              createNoun={t("newCase.createNounClient")}
              createLabel={(name) => t("newCase.createEntity", { name })}
              reuseLabel={(name) => t("newCase.reuseEntity", { name })}
              inputClassName={inputCls}
            />
            {/* ⚖ D-98 affordance (see `isNewMatter`) — shown for `typed` too. */}
            {hasNewName(clientValue) && (
              <label className="mt-1.5 flex items-center gap-1.5 text-body-sm text-coffee-700 dark:text-cream-300">
                <input
                  type="checkbox"
                  data-testid="new-case-client-isperson"
                  checked={isPerson}
                  onChange={(e) => setIsPerson(e.target.checked)}
                  className="rounded border-cream-300"
                />
                {t("newCase.clientIsPerson")}
              </label>
            )}

            {/* MATTER — pick existing or type new (OPTIONAL); scoped to the client */}
            <label className={cn(labelCls, "mt-4")}>
              {t("newCase.matterLabel")}{" "}
              <span className="font-normal normal-case tracking-normal text-cream-400">
                {t("newCase.optionalTag")}
              </span>
            </label>
            <NameOrPickField
              data-testid="new-case-matter"
              options={clientValue?.mode === "existing" ? matterOptions : []}
              value={matterValue}
              onChange={(v) => {
                setMatterValue(v);
                // Auto-type a NEW matter: a picked/typed name that folds to a
                // vocabulary row takes that row's (authoritative) matterType;
                // else category inference. Only EXACT folded matches set the
                // select, so a manual type re-pick survives any non-matching edit.
                // ⚠ Must use `hasNewName`, NOT `isExplicitCreate`: this is meant
                // to fire PER KEYSTROKE, and since ⚖ D-98 typing emits `typed`.
                // Gating it on `mode:"new"` would silently reduce it to a
                // commit-time-only helper — a live regression wearing the shape
                // of "the vocabulary just stopped matching".
                if (hasNewName(v)) {
                  const labels = Object.fromEntries(
                    RUNTIME_MATTER_TYPES.map((mt) => [mt, t(`newCase.matterTypes.${mt}`)]),
                  );
                  const resolved = resolveMatterTypeForName(
                    v.name,
                    vocabulary ?? [],
                    labels,
                    RUNTIME_MATTER_TYPES,
                  );
                  if (resolved) setMatterType(resolved);
                }
              }}
              disabled={!clientValue}
              placeholder={
                clientValue ? t("newCase.matterPlaceholder") : t("newCase.matterPlaceholderNoClient")
              }
              createNoun={t("newCase.createNounMatter")}
              createLabel={(name) => t("newCase.createEntity", { name })}
              reuseLabel={(name) => t("newCase.reuseEntity", { name })}
              inputClassName={inputCls}
              suggestions={matterSuggestions}
              suggestionsLabel={t("newCase.matterSuggestionsLabel")}
              // Concept guard (operator 2026-07-07): matters are short
              // category-style portfolio labels — the cap alone blocks
              // case-title-style names ("Indenização — Danos Morais" = 26).
              // 24 fits the longest common category ("Contencioso Tributário"
              // = 22). Authoritative enforcement lands lexserver-side with the
              // platform matter-vocabulary (see the 2026-07-07 handoff).
              maxLength={24}
            />
            {/* ⚠ Rendered for a NEW matter under a client, AND for the
                PARENTLESS case. The second is the fix for the fresh-org
                blocker: with no client there is no matter to inherit a type
                from, so the server REQUIRES `matterType` — and this select was
                the only way to supply it while being hidden in exactly that
                state. The field was gated on the presence of the thing whose
                ABSENCE makes it mandatory.
                ⚠ It stays hidden when an EXISTING client+matter is chosen —
                that case genuinely inherits its type, and showing a type
                picker there would invite a contradiction with the matter's own
                record. This narrows the condition; it does not remove it. */}
            {(isNewMatter || !clientValue) && (
              <div className="mt-1.5">
                <label className={labelCls}>{t("newCase.matterTypeLabel")}</label>
                <select
                  data-testid="new-case-matter-type"
                  value={matterType}
                  onChange={(e) => setMatterType(e.target.value)}
                  className={inputCls}
                >
                  {RUNTIME_MATTER_TYPES.map((mt) => (
                    <option key={mt} value={mt}>
                      {t(`newCase.matterTypes.${mt}`)}
                    </option>
                  ))}
                </select>
                {/* Concept guard (solution §3.1): matters are practice-area
                    PORTFOLIOS, not case titles — steer the naming here. */}
                <p className="mt-1 text-caption text-cream-500">{t("newCase.matterPortfolioHint")}</p>
              </div>
            )}

            {/* "Create now, assign later" — when no client is chosen the case is
                born unassigned; surface where it lands + that it can be assigned. */}
            {!clientValue && (
              <p
                data-testid="new-case-assign-later-hint"
                className="mt-2 rounded-lg bg-cream-100/60 px-2.5 py-2 text-caption text-cream-500 dark:bg-coffee-800/40 dark:text-cream-400"
              >
                {t("newCase.assignLaterHint")}
              </p>
            )}

            {/* TITLE (required) */}
            <label className={cn(labelCls, "mt-4")}>{t("newCase.titleLabel")}</label>
            <input
              ref={titleRef}
              data-testid="new-case-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void handleCreate();
                }
              }}
              placeholder={t("newCase.titlePlaceholder")}
              maxLength={200}
              className={inputCls}
            />

            {/* Parties (optional, repeatable) — feed the conflict scan */}
            <div className="mb-1 mt-4 flex items-center justify-between">
              <span className="text-caption font-medium uppercase tracking-wide text-cream-500">
                {t("newCase.partiesLabel")}
              </span>
              <span className="text-caption text-cream-500">{t("newCase.partiesHint")}</span>
            </div>
            <div className="flex flex-col gap-1.5">
              {parties.map((party, i) => (
                <div key={i} className="flex items-center gap-1.5" data-testid="new-case-party-row">
                  <input
                    data-testid="new-case-party-name"
                    value={party.name}
                    onChange={(e) =>
                      setParties((prev) => prev.map((p, j) => (j === i ? { ...p, name: e.target.value } : p)))
                    }
                    placeholder={t("newCase.partyNamePlaceholder")}
                    className={cn(inputCls, "flex-1")}
                  />
                  <input
                    data-testid="new-case-party-role"
                    value={party.role}
                    onChange={(e) =>
                      setParties((prev) => prev.map((p, j) => (j === i ? { ...p, role: e.target.value } : p)))
                    }
                    placeholder={t("newCase.partyRolePlaceholder")}
                    className={cn(inputCls, "w-28 shrink-0")}
                  />
                  <button
                    type="button"
                    onClick={() => setParties((prev) => prev.filter((_, j) => j !== i))}
                    aria-label={t("newCase.removeParty")}
                    className="shrink-0 rounded-lg p-1.5 text-cream-500 hover:bg-cream-100/70 hover:text-coffee-700 dark:hover:bg-coffee-800/60"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                data-testid="new-case-add-party"
                onClick={() => setParties((prev) => [...prev, { name: "", role: "" }])}
                className="flex w-fit items-center gap-1.5 rounded-lg px-2 py-1 text-body-sm text-coffee-600 hover:bg-cream-100/70 dark:text-cream-300 dark:hover:bg-coffee-800/60"
              >
                <Plus size={14} />
                {t("newCase.addParty")}
              </button>
            </div>

            {error && (
              <p
                data-testid="new-case-error"
                className="mt-3 rounded-lg bg-red-50 p-2.5 text-body-sm text-red-700 dark:bg-red-950 dark:text-red-400"
              >
                {error}
              </p>
            )}

            <div className={dialogFooterCls}>
              <button
                type="button"
                onClick={onClose}
                disabled={creating}
                className={dialogCancelBtnCls}
              >
                {t("actions.cancel")}
              </button>
              <button
                type="button"
                data-testid="new-case-submit"
                onClick={() => void handleCreate()}
                disabled={!canCreate}
                className={dialogPrimaryBtnCls(canCreate)}
              >
                {creating && <Loader2 size={13} className="animate-spin" />}
                {t("newCase.create")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Advisory conflict-of-interest panel shown after a create that returned hits.
 *  The case is already created — this is an acknowledgment, not a gate. */
function ConflictReview({
  conflicts,
  onOpen,
  onClose,
}: {
  conflicts: CaseConflictHit[];
  onOpen: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div data-testid="new-case-conflicts">
      <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-300/60 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-950/30">
        <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="min-w-0">
          <p className="text-body-sm font-medium text-amber-800 dark:text-amber-300">{t("newCase.conflictTitle")}</p>
          <p className="mt-0.5 text-caption text-amber-700/90 dark:text-amber-400/80">
            {t("newCase.conflictSubtitle")}
          </p>
        </div>
      </div>
      <ul className="flex flex-col gap-1.5">
        {conflicts.map((c, i) => (
          <li
            key={i}
            data-testid="new-case-conflict-row"
            className="rounded-lg border border-cream-200/70 bg-cream-50/70 px-3 py-2 text-body-sm text-coffee-800 dark:border-coffee-700/60 dark:bg-coffee-800/40 dark:text-cream-200"
          >
            <span className="font-medium">
              {c.proposedName}
              {c.proposedRole ? ` (${c.proposedRole})` : ""}
            </span>{" "}
            {t("newCase.conflictAlsoIn")}{" "}
            <span className="font-medium">«{c.existingCaseTitle}»</span>
            {c.existingRole ? ` — ${c.existingRole}` : ""}
          </li>
        ))}
      </ul>
      <div className="mt-5 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg px-3 py-1.5 text-body-sm text-cream-500 hover:bg-cream-100/70 dark:text-cream-400 dark:hover:bg-coffee-800/60"
        >
          {t("actions.close")}
        </button>
        <button
          type="button"
          data-testid="new-case-conflicts-open"
          onClick={onOpen}
          className="rounded-lg bg-gold-500 px-3.5 py-1.5 text-body-sm font-medium text-coffee-950 hover:bg-gold-400"
        >
          {t("newCase.openAnyway")}
        </button>
      </div>
    </div>
  );
}
